import { useState } from 'react'
import { Link, router, useForm } from '@inertiajs/react'
import { ArrowLeft, Archive } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type CrmFundraisingDonation from '@/types/serializers/CrmFundraisingDonation'
import {
  DONATION_STATUS_LABELS,
  STATUS_BADGE_VARIANT,
} from '@/lib/fundraising-labels'

interface Props {
  donation: CrmFundraisingDonation
  /** When rendered inside a detail sheet: hides full-page chrome and routes
   *  mutations through a partial reload + onMutated callback instead of a
   *  full-page redirect. */
  inSheet?: boolean
  /** Called after a successful mutation so the sheet can refetch and the index refresh. */
  onMutated?: () => void
  /** Called when the record should no longer be shown in the sheet (e.g. archived). */
  onClose?: () => void
}

export default function DonationShow({ donation, inSheet = false, onMutated, onClose }: Props) {
  const [cancelOpen, setCancelOpen] = useState(false)

  const statusKey = (donation as any).status_key ?? (typeof donation.status === 'object' ? '' : donation.status)
  const statusLabel = typeof donation.status === 'object' ? donation.status.label : String(donation.status)

  // Cash ("espèces") donations are immutable: entered directly as received and
  // never cancellable (US11 / #128), so the cancel control is hidden for them.
  const isCash = donation.payment_method_cash
  const canReceive = statusKey === 'promised'
  const canCancel = (statusKey === 'promised' || statusKey === 'received') && !isCash
  const isTerminal = statusKey === 'cancelled'

  // In a sheet, preserve the open sheet across the mutation's redirect_back and
  // refetch via onMutated; on the full page, keep the default redirect behavior.
  const mutationOpts = onMutated
    ? { preserveScroll: true, preserveState: true, onSuccess: () => onMutated() }
    : {}

  function handleReceive() {
    router.post(`/fundraising/donations/${donation.id}/transition`, {
      event: 'receive',
    }, mutationOpts)
  }

  function handleArchive() {
    // In a sheet, archiving removes the row from the kept scope, so refetching
    // the same id would 404 — close the sheet instead of refetching.
    const archiveOpts = onClose
      ? { preserveScroll: true, preserveState: true, onSuccess: () => onClose() }
      : {}
    router.patch(`/fundraising/donations/${donation.id}/archive`, {}, archiveOpts)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          {!inSheet && (
            <Link
              href="/fundraising/donations"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Dons
            </Link>
          )}
          <h1 className="text-2xl font-bold tracking-tight">
            Don de {donation.donor_name}
          </h1>
          <div className="flex items-center gap-3">
            <Badge variant={STATUS_BADGE_VARIANT[statusKey] ?? 'outline'}>
              {statusLabel}
            </Badge>
            <span className="text-lg font-semibold tabular-nums">
              {donation.amount_formatted}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Archive className="mr-1.5 h-4 w-4" />
                Archiver
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archiver ce don ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Le don sera archive et ne sera plus visible dans la liste par defaut.
                  Vous pourrez le restaurer a tout moment.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchive}>
                  Archiver
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Transition buttons */}
      {!isTerminal && (
        <div className="flex items-center gap-2">
          {canReceive && (
            <Button size="sm" onClick={handleReceive}>
              Encaisser
            </Button>
          )}
          {canCancel && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setCancelOpen(true)}
            >
              Annuler
            </Button>
          )}
        </div>
      )}

      <Separator />

      {/* Detail cards */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Informations du don</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Montant</dt>
                <dd className="font-medium tabular-nums">{donation.amount_formatted}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Date</dt>
                <dd>{new Date(donation.donation_date).toLocaleDateString('fr-FR')}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Campagne</dt>
                <dd>{donation.campaign_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Mode de paiement</dt>
                <dd>{donation.payment_method_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Nature du don</dt>
                <dd>{donation.nature_name}</dd>
              </div>
              {donation.transaction_reference && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Reference</dt>
                  <dd className="font-mono text-xs">{donation.transaction_reference}</dd>
                </div>
              )}
              {donation.created_at && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Cree le</dt>
                  <dd>{new Date(donation.created_at).toLocaleDateString('fr-FR')}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Donateur</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Nom</dt>
                <dd className="font-medium">{donation.donor_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Type</dt>
                <dd>
                  <Badge variant="outline">{donation.donor_type}</Badge>
                </dd>
              </div>
              {donation.contact_name && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Contact</dt>
                  <dd>{donation.contact_name}</dd>
                </div>
              )}
              {donation.assignee_name && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Responsable</dt>
                  <dd>{donation.assignee_name}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Cancellation reason if cancelled */}
      {statusKey === 'cancelled' && donation.cancellation_reason && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Motif d'annulation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{donation.cancellation_reason}</p>
          </CardContent>
        </Card>
      )}

      {/* Cancel dialog */}
      <CancelDonationDialog
        donationId={donation.id}
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        onMutated={onMutated}
      />
    </div>
  )
}

function CancelDonationDialog({
  donationId,
  open,
  onOpenChange,
  onMutated,
}: {
  donationId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onMutated?: () => void
}) {
  const form = useForm({
    event: 'cancel',
    cancellation_reason: '',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.post(`/fundraising/donations/${donationId}/transition`, {
      preserveScroll: Boolean(onMutated),
      preserveState: Boolean(onMutated),
      onSuccess: () => {
        form.reset()
        onOpenChange(false)
        onMutated?.()
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Annuler ce don ?</DialogTitle>
            <DialogDescription>
              Cette action est irreversible. Veuillez indiquer le motif d'annulation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-4">
            <Label htmlFor="cancel_reason">Motif d'annulation *</Label>
            <Input
              id="cancel_reason"
              value={form.data.cancellation_reason}
              onChange={(e) => form.setData('cancellation_reason', e.target.value)}
              placeholder="Raison de l'annulation..."
              required
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Retour
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={form.processing || !form.data.cancellation_reason.trim()}
            >
              {form.processing ? 'Annulation...' : 'Confirmer l\'annulation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
